import React from 'react';

import PropTypes from 'prop-types';
import * as cn from 'classnames';
import { VscTriangleUp } from 'react-icons/vsc';

import { Text } from 'modules/common';

const renderType = {
  default: {
    text: '',
    background: 'bg-light',
    arrow: 'text-light',
  },
  secondary: {
    text: 'text-primitive-white',
    background: 'bg-primitive-black',
    arrow: 'text-primitive-black',
  },
};
const renderArrowAlign = {
  top: '-top-4',
  left: '-left-4 -rotate-90',
  right: '-right-4 rotate-90',
  bottom: '-bottom-4 rotate-180',
};
const renderArrowBorderAlign = {
  top: 'mt-[-1px]',
  left: 'ml-[-1px]',
  right: 'mr-[-1px]',
  bottom: 'mb-[-1px]',
};

export const TooltipWrapper = ({ className, isBlock, children, ...rest }) => (
  <div
    className={cn(
      'has-tooltip group',
      className,
      isBlock ? 'block' : 'inline-block'
    )}
    {...rest}
  >
    {children}
  </div>
);

TooltipWrapper.defaultProps = {
  className: '',
  children: false,
  isBlock: true,
};

TooltipWrapper.propTypes = {
  className: PropTypes.string,
  isBlock: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
};

function Tooltip({
  className,
  type,
  description,
  title,
  isShow,
  arrowAlign,
  withArrow,
  arrowClassName,
  isCenter,
}) {
  if (!isShow) return null;

  const $type = renderType?.[type];

  return (
    <div
      className={cn(
        'flex flex-col gap-ds-8 shadow-tooltip rounded p-ds-16 w-72 relative rotate mt-2',
        'tooltip transition-all ease-in-out duration-100 opacity-0 group-hover:opacity-100 invisible group-hover:visible',
        $type?.background,
        className,
        {
          border: type === 'default',
          '!top-1/2 !left-1/2 !-translate-y-1/2 !-translate-x-1/2': isCenter,
        }
      )}
    >
      {withArrow ? (
        <>
          {/* add arrow border start */}
          {type === 'default' && (
            <div
              className={cn(
                'absolute',
                renderArrowAlign?.[arrowAlign] || renderArrowAlign?.top,
                arrowClassName,
                renderArrowBorderAlign?.[arrowAlign] ||
                  renderArrowBorderAlign?.top
              )}
            >
              <VscTriangleUp className={cn('h-6 w-6', 'text-gray-200')} />
            </div>
          )}
          {/* add arrow border end */}

          <div
            className={cn(
              'absolute',
              renderArrowAlign?.[arrowAlign] || renderArrowAlign?.top,
              arrowClassName
            )}
          >
            <VscTriangleUp className={cn('h-6 w-6', $type.arrow)} />
          </div>
        </>
      ) : null}

      {Boolean(title) && (
        <Text type="caption" weight="semibold" className={$type?.text}>
          {title}
        </Text>
      )}

      <Text type="caption" className={$type?.text}>
        {description}
      </Text>
    </div>
  );
}

Tooltip.defaultProps = {
  title: '',
  description: '',
  className: '',
  isShow: true,
  isCenter: false,
  arrowAlign: '',
  arrowClassName: '',
  withArrow: false,
  type: 'default',
};

Tooltip.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string,
  arrowClassName: PropTypes.string,
  isShow: PropTypes.bool,
  isCenter: PropTypes.bool,
  withArrow: PropTypes.bool,
  arrowAlign: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(Object.keys(renderArrowAlign)),
  ]),
  type: PropTypes.oneOf(['default', 'primary', 'secondary']),
};

export default Tooltip;
